<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  label: {
    type: String,
    required: false,
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Search...',
  },
  primaryKey: {
    type: String,
    required: false,
    default: 'id',
  },
  labelKey: {
    type: String,
    required: false,
    default: 'name',
  },
  labelDescription: {
    type: String,
    required: false,
    default: 'breadcrumb',
  },
  modelValue: {
    type: Object,
    required: false,
    default: null,
  },
})

const emits = defineEmits(['update:modelValue'])

const results = ref([])
const searchMethod = ref(null)
const keyword = ref('')
const selectedItem = ref(null)
const searchMethods = ref([
  { id: 'Starts With', value: null },
  { id: 'Exact Match', value: 'match' },
  { id: 'Contains', value: 'contains' },
  { id: 'Ends With', value: 'endsWith' },
])

onMounted(() => {
  if (props.modelValue) {
    selectedItem.value = props.modelValue
    select(props.modelValue)
  }
})

const fetch = () => {
  axios.get(route('hq.categories.search'), {
    params: {
      search: keyword.value,
      search_method: searchMethod.value,
    },
  }).then(res => {
    results.value = res.data
  })
}

const search = () => {
  if (keyword.value.length >= 3) {
    debounce(fetch(), 300)
  } else {
    results.value = []
  }
}

const select = (item) => {
  selectedItem.value = item
  emits('update:modelValue', item)
}

const resetSearch = () => {
  selectedItem.value = null
  results.value = []
  emits('update:modelValue', '')
}

const debounce = (func, wait = 100) => {
  let timeout
  return function(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      func.apply(this, args)
    }, wait)
  }
}
</script>

<template>
  <div class="relative flex items-center">
    <label :for="label" class="block text-sm font-medium leading-5 text-gray-700">
      <span class="text-gray-500 text-sm">{{ label }}</span>
    </label>
    <div class="flex relative border border-gray-200 w-72">
      <div v-if="selectedItem" class="bg-white shadow-sm py-1 px-1 block sm:text-sm w-full">
        <div
          class="inline-flex items-center justify-center px-2.5 py-1.5 text-xs font-medium leading-4 bg-blue-100 text-blue-800">
          <span class="mr-1">{{ selectedItem[labelKey] }}</span>
          <button @click="resetSearch">
            <XMarkIcon class="w-3 h-3" />
          </button>
        </div>
      </div>
      <input
        v-else
        :id="label"
        type="text"
        v-model="keyword"
        :placeholder="placeholder"
        class="focus:ring-0 focus:border-transparent block text-sm !border-none"
        @input="search"
      >
      <div v-if="!selectedItem" class="relative flex items-center w-52 rounded overflow-hidden bg-white">
        <label for="searchMethod" class="sr-only">{{ __('Search Method') }}</label>
        <select @change="fetch" v-model="searchMethod" id="searchMethod" name="currency"
                class="focus:ring-0 focus:border-transparent h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 text-sm">
          <option v-for="searchMethod in searchMethods" :value="searchMethod.value">{{ searchMethod.id }}</option>
        </select>
      </div>
      <div v-if="results && !selectedItem" class="bg-white shadow-lg absolute mt-10 w-full z-50">
        <ul class="overflow-y-scroll max-h-64">
          <li class="flex-col hover:bg-gray-100 px-5 py-2" v-for="item in results" :key="item[primaryKey]"
              @click="select(item)">
            <div class="text-black">
              {{ item[labelKey] }}
            </div>
            <div class="text-xxs text-gray-400" v-if="item[labelDescription]">
              {{ item[labelDescription] }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
